import React, { useState, useCallback, useEffect } from 'react';
import { withSnackbar } from '../../utils/SnackbarHOC';
import { Box, Chip, CircularProgress, Skeleton, Tooltip, Typography } from '@mui/material';
import AddTypeDocumentDialog from './components/AddTypeDocumentDialog';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import Add from '@mui/icons-material/Add';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import FolderCopyOutlined from '@mui/icons-material/FolderCopyOutlined';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import { AppButton } from '../../components/UI';

import { getAllTypeDocuments, addTypeDocument } from '../../services/TypeDocumentService';
import { getAllContrats } from '../../services/ContratService';
import {
  getAllDocumentContrats,
  addDocumentContrat,
  deleteDocumentContrat,
  updateObligation,
} from '../../services/DocumentContratService';

const card = { borderRadius: '14px', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.05)' };

// A document is, for each contract: not requested, optional, or required.
const STATES = [
  { value: 'none', label: '—', title: 'Non demandé', color: '#94a3b8', bg: '#fff', border: '#e2e8f0' },
  { value: 'optional', label: 'Facultatif', title: 'Demandé mais pas exigé', color: '#03407b', bg: '#ebf4fb', border: '#aed6f1' },
  { value: 'required', label: 'Obligatoire', title: 'Pièce exigée', color: '#b91c1c', bg: '#fef2f2', border: '#fca5a5' },
];

const stateOf = (link) => (!link ? 'none' : link.obligation ? 'required' : 'optional');

const StateSelector = ({ value, onChange, busy }) => (
  <Box sx={{ display: 'inline-flex', p: 0.4, gap: 0.4, borderRadius: '10px', background: '#f1f5f9', position: 'relative' }}>
    {STATES.map((s) => {
      const active = value === s.value;
      return (
        <Tooltip key={s.value} title={s.title}>
          <Box
            role="radio"
            aria-checked={active}
            tabIndex={0}
            onClick={() => !busy && !active && onChange(s.value)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !busy && !active && onChange(s.value)}
            sx={{
              minWidth: s.value === 'none' ? 32 : 84, px: 1, py: 0.5, borderRadius: '8px', textAlign: 'center',
              fontSize: '0.74rem', fontWeight: 700, userSelect: 'none', transition: 'all 0.15s ease',
              cursor: busy ? 'progress' : active ? 'default' : 'pointer',
              color: active ? s.color : '#94a3b8',
              background: active ? s.bg : 'transparent',
              border: `1px solid ${active ? s.border : 'transparent'}`,
              boxShadow: active ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
              opacity: busy ? 0.6 : 1,
              '&:hover': !active && !busy ? { color: s.color } : undefined,
              '&:focus-visible': { outline: '2px solid #aed6f1' },
            }}
          >
            {s.label}
          </Box>
        </Tooltip>
      );
    })}
    {busy && <CircularProgress size={14} sx={{ position: 'absolute', right: -20, top: '50%', mt: '-7px' }} />}
  </Box>
);

const DocumentsContrat = ({ snackbarShowMessage }) => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contrats, setContrats] = useState([]);
  const [typeDocuments, setTypeDocuments] = useState([]);
  const [links, setLinks] = useState([]);
  const [busyCell, setBusyCell] = useState(null);

  const reloadLinks = useCallback(async () => {
    const { data } = await getAllDocumentContrats();
    setLinks(data);
    return data;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [contratsRes, typeDocsRes, docContratsRes] = await Promise.all([
          getAllContrats(),
          getAllTypeDocuments(),
          getAllDocumentContrats(),
        ]);
        setContrats(contratsRes.data);
        setTypeDocuments(typeDocsRes.data);
        setLinks(docContratsRes.data);
      } catch (error) {
        snackbarShowMessage("Erreur de chargement des données.", 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [snackbarShowMessage]);

  const handleAddTypeDocument = useCallback(async (newTypeName) => {
    await addTypeDocument(newTypeName);
    const { data } = await getAllTypeDocuments();
    setTypeDocuments(data);
    snackbarShowMessage(`Type de document « ${newTypeName} » ajouté.`, 'success');
  }, [snackbarShowMessage]);

  const findLink = (list, contratId, docId) => list.find((l) => l.contratId === contratId && l.type_documentId === docId);

  // Moves a (contract, document) cell to a new state with the existing API calls.
  const changeState = useCallback(async (contratId, docId, next) => {
    const key = `${contratId}|${docId}`;
    setBusyCell(key);
    try {
      let link = findLink(links, contratId, docId);
      if (next === 'none') {
        await deleteDocumentContrat({ contratId, type_documentId: docId });
        setLinks((prev) => prev.filter((l) => !(l.contratId === contratId && l.type_documentId === docId)));
        snackbarShowMessage('Document retiré du contrat.', 'info');
        return;
      }
      if (!link) {
        await addDocumentContrat({ contratId, type_documentId: docId });
        link = findLink(await reloadLinks(), contratId, docId);
      }
      const wantRequired = next === 'required';
      if (link && Boolean(link.obligation) !== wantRequired) {
        if (!link.id) throw new Error('missing link id');
        await updateObligation(link.id, wantRequired);
        setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, obligation: wantRequired } : l)));
      }
      snackbarShowMessage(wantRequired ? 'Document obligatoire pour ce contrat.' : 'Document facultatif pour ce contrat.', 'success');
    } catch (error) {
      snackbarShowMessage("Erreur lors de l'enregistrement.", 'error');
      reloadLinks().catch(() => {});
    } finally {
      setBusyCell(null);
    }
  }, [links, reloadLinks, snackbarShowMessage]);

  const countFor = (contratId) => {
    const l = links.filter((x) => x.contratId === contratId);
    return { total: l.length, required: l.filter((x) => x.obligation).length };
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ebf4fb', color: '#03407b' }}>
            <FolderCopyOutlined />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>Gestion des documents</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>Définissez les pièces à fournir par les collaborateurs pour chaque type de contrat</Typography>
          </Box>
        </Box>
        <Box sx={{ ml: { md: 'auto' } }}>
          <AppButton variant="primary" size="small" startIcon={<Add fontSize="small" />} onClick={() => setOpenAddDialog(true)}>
            Nouveau type de document
          </AppButton>
        </Box>
      </Box>

      {!loading && contrats.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1.5 }}>
          {contrats.map((c) => {
            const { total, required } = countFor(c.id);
            return (
              <Box key={c.id} sx={{ ...card, px: 1.75, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ebf4fb', color: '#03407b' }}>
                  <AssignmentOutlined sx={{ fontSize: 19 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>{c.nom_contrat}</Typography>
                  <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>
                    {total} document{total > 1 ? 's' : ''} · <b style={{ color: required ? '#b91c1c' : '#64748b' }}>{required} obligatoire{required > 1 ? 's' : ''}</b>
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ ...card, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid #f1f5f9', background: '#fafcff' }}>
          <InfoOutlined sx={{ color: '#03407b', fontSize: 17 }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
            Pour chaque contrat, choisissez si la pièce est <b>non demandée (—)</b>, <b style={{ color: '#03407b' }}>facultative</b> ou <b style={{ color: '#b91c1c' }}>obligatoire</b>. Enregistré automatiquement.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: '10px' }} />)}
          </Box>
        ) : typeDocuments.length === 0 ? (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, color: '#94a3b8' }}>
            <DescriptionOutlined sx={{ fontSize: 40 }} />
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#475569' }}>Aucun type de document</Typography>
            <AppButton variant="primary" size="small" startIcon={<Add fontSize="small" />} onClick={() => setOpenAddDialog(true)}>
              Créer le premier type
            </AppButton>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { px: 2, py: 1.1, borderBottom: '1px solid #f1f5f9', textAlign: 'left' } }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase', width: '30%' }}>
                    Type de document
                  </Box>
                  {contrats.map((c) => (
                    <Box component="th" key={c.id} sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
                      {c.nom_contrat}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {typeDocuments.map((doc) => (
                  <Box component="tr" key={doc.id} sx={{ '&:hover': { background: '#fafcff' } }}>
                    <Box component="td">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionOutlined sx={{ fontSize: 18, color: '#03407b' }} />
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' }}>{doc.type_document}</Typography>
                        {links.filter((l) => l.type_documentId === doc.id).length === 0 && (
                          <Chip size="small" label="Utilisé nulle part" sx={{ height: 18, fontSize: '0.64rem', color: '#94a3b8', background: '#f1f5f9' }} />
                        )}
                      </Box>
                    </Box>
                    {contrats.map((c) => (
                      <Box component="td" key={c.id}>
                        <StateSelector
                          value={stateOf(findLink(links, c.id, doc.id))}
                          busy={busyCell === `${c.id}|${doc.id}`}
                          onChange={(next) => changeState(c.id, doc.id, next)}
                        />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <AddTypeDocumentDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onAddType={handleAddTypeDocument}
        existingNames={typeDocuments.map((d) => d.type_document)}
      />
    </Box>
  );
};

export default withSnackbar(DocumentsContrat);
